import { useMemo, useState } from 'react';
import { DataTable, EmptyState, StatusPill, Pagination, type ColumnDef } from '@vnetwork/ui/data';
import { Section } from '@vnetwork/ui/layout';
import { paginationView } from '../../pagination-summary';
import { formatDateTime } from '@/lib/format';
import { useCustomerMailerTasks, type CustomerMailerTaskRow } from '@/api/customer-mailers';
import { resolveMailerTaskStatus, mailerSource } from './mailer-status-tones';
import { TabError, TabLoading } from '../tab-states';

const PAGE_SIZE = 20;

/** Join a recipient array to a single "a, b" string (or em dash). */
function joinRecipients(list?: string[]): string {
  if (!list || list.length === 0) return '—';
  return list.join(', ');
}

/** Best "sent" timestamp for a task: performed → send → created. */
function sentAt(row: CustomerMailerTaskRow): string | null | undefined {
  return row.mailer_performed_at ?? row.mailer_send_at ?? row.mailer_created_at;
}

/** Received / bounced delivery indicator for the row (received wins). */
function DeliveryIndicator({ row }: { row: CustomerMailerTaskRow }) {
  if (row.mailer_received_at) {
    return <StatusPill tone="success">Received {formatDateTime(row.mailer_received_at)}</StatusPill>;
  }
  if (row.mailer_bounced_at) {
    return <StatusPill tone="danger">Bounced {formatDateTime(row.mailer_bounced_at)}</StatusPill>;
  }
  return <span style={{ color: 'var(--vn-fg-muted)' }}>—</span>;
}

function useEmailHistoryColumns() {
  return useMemo<ColumnDef<CustomerMailerTaskRow, unknown>[]>(
    () => [
      {
        id: 'mailer_subject',
        header: 'Subject',
        cell: ({ row }) => (
          <span style={{ fontWeight: 'var(--vn-font-weight-medium)', overflowWrap: 'anywhere' }}>
            {row.original.mailer_subject || '—'}
          </span>
        ),
      },
      {
        id: 'mailer_receiver',
        header: 'Recipients',
        cell: ({ row }) => (
          <span style={{ overflowWrap: 'anywhere' }}>{joinRecipients(row.original.mailer_receiver)}</span>
        ),
      },
      {
        id: 'mailer_status',
        header: 'Status',
        cell: ({ row }) => {
          const { tone, label } = resolveMailerTaskStatus(
            row.original.mailer_status,
            row.original.receipt_status,
          );
          return <StatusPill tone={tone}>{label}</StatusPill>;
        },
      },
      {
        id: 'sent_at',
        header: 'Sent',
        cell: ({ row }) => formatDateTime(sentAt(row.original)),
      },
      {
        id: 'delivery',
        header: 'Delivery',
        cell: ({ row }) => <DeliveryIndicator row={row.original} />,
      },
      {
        id: 'mailer_referrer_type',
        header: 'Source',
        cell: ({ row }) => mailerSource(row.original.mailer_referrer_type),
      },
    ],
    [],
  );
}

/** Email history — server-paginated delivery log for the customer's mailers. */
export function EmailHistorySection({ customerId }: { customerId: number }) {
  const [page, setPage] = useState(1);
  const columns = useEmailHistoryColumns();
  const { data, isLoading, isError, refetch } = useCustomerMailerTasks(customerId, {
    page,
    page_size: PAGE_SIZE,
  });

  const rows = data?.results ?? [];
  const total = data?.pagination?.total ?? rows.length;
  const pageCount = data?.pagination?.total_pages ?? 1;

  return (
    <Section title="Email History">
      {isError ? (
        <TabError title="Failed to load email history" onRetry={() => refetch()} />
      ) : isLoading ? (
        <TabLoading rows={6} />
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          enableSorting={false}
          getRowId={(r) => String(r.mailer_id)}
          emptyState={
            <EmptyState title="No emails sent" description="No email history for this customer yet." />
          }
          footer={(() => {
            const view = paginationView(page, PAGE_SIZE, rows.length, total, pageCount);
            return (
              <Pagination page={page} pageCount={view.pageCount} onPageChange={setPage} summary={view.summary} />
            );
          })()}
        />
      )}
    </Section>
  );
}
